import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search as SearchIcon, Refresh, LocalShipping, EditNote, Done, FilterList } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Delivery list -- outbound dispatches raised against sales orders. Mirrors
// RfqPage.tsx's shape: stat cards computed client-side, a filter bar, a
// single results table, row click -> detail. Deliveries are created from a
// Sales Order's detail page, not from here.
// ---------------------------------------------------------------------------

type DeliveryStatus = 'DRAFT' | 'DISPATCHED' | 'DELIVERED' | 'CANCELLED';

interface DeliveryRow {
  id: number;
  delivery_number: string;
  sales_order_id: number | string;
  customer_name?: string;
  dispatch_date?: string | null;
  transporter?: string | null;
  vehicle_no?: string | null;
  status: DeliveryStatus;
}

const STATUS_OPTIONS: DeliveryStatus[] = ['DRAFT', 'DISPATCHED', 'DELIVERED', 'CANCELLED'];

const STATUS_TONE: Record<DeliveryStatus, StatusTone> = {
  DRAFT: 'neutral',
  DISPATCHED: 'info',
  DELIVERED: 'success',
  CANCELLED: 'error',
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const DeliveryPage: React.FC = () => {
  const navigate = useNavigate();

  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<DeliveryStatus | ''>('');
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ deliveries: DeliveryRow[]; count: number }>('/deliveries')
      .then((res) => setDeliveries(res.deliveries || []))
      .catch((err: any) => setError(err.message || 'Failed to load deliveries'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handle = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAll]);

  const filtered = deliveries.filter((d) => {
    const matchesStatus = !statusFilter || d.status === statusFilter;
    const matchesSearch =
      !search ||
      d.delivery_number?.toLowerCase().includes(search.toLowerCase()) ||
      d.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.vehicle_no?.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalCount = deliveries.length;
  const draftCount = deliveries.filter((d) => d.status === 'DRAFT').length;
  const dispatchedCount = deliveries.filter((d) => d.status === 'DISPATCHED').length;
  const deliveredCount = deliveries.filter((d) => d.status === 'DELIVERED').length;

  const hasFilters = !!(statusFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Deliveries"
        subtitle="Track outbound dispatches from sales orders through to proof of delivery"
        actions={
          <IconButton onClick={fetchAll} disabled={loading}>
            <Refresh />
          </IconButton>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Deliveries" value={loading ? null : totalCount} icon={<LocalShipping />} color="#2563eb" />
        <StatCard title="Draft" value={loading ? null : draftCount} icon={<EditNote />} color="#64748b" />
        <StatCard title="Dispatched" value={loading ? null : dispatchedCount} icon={<LocalShipping />} color="#0284c7" />
        <StatCard title="Delivered" value={loading ? null : deliveredCount} icon={<Done />} color="#059669" />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Delivery #, customer or vehicle"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DeliveryStatus | '')}
          sx={{ minWidth: 170 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Delivery', 'Customer', 'Dispatch Date', 'Transporter', 'Vehicle No', 'Status'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchAll} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No deliveries match these filters"
                      hint="Clear the filters, or create a delivery from a sales order."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((d) => (
                  <TableRow
                    key={d.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/sales/deliveries/${d.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={d.delivery_number} tint="#0284c7" icon={<LocalShipping fontSize="small" />} />
                    </TableCell>
                    <TableCell>{d.customer_name || '—'}</TableCell>
                    <TableCell>{fmtDate(d.dispatch_date)}</TableCell>
                    <TableCell>{d.transporter || '—'}</TableCell>
                    <TableCell>{d.vehicle_no || '—'}</TableCell>
                    <TableCell>
                      <StatusDot label={d.status} tone={STATUS_TONE[d.status]} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default DeliveryPage;
